import {
  Button,
  Divider,
  Drawer,
  Form,
  Input,
  InputRef,
  Select,
  Space,
  Tooltip,
} from "antd";
import { useContext, useEffect, useState, useRef } from "react";
import { IBaseDrawerModel, IDrawerModel, IOrganisationDetails } from "../../../Types";
import { useClient } from "../../Context/ClientContext";
import { notificationContext } from "../../Common/Routing/PageRoute";
import OrganisationsService, {
  ISubUnitData,
  ISubUnitOptionsList,
  ISubUnitRequestModel,
} from "../../Services/Organisations/OrganisationsService";
import { IApiResponse } from "../../../Utilities/ApiUtility.axios";
import { IIdNameModel } from "../../Services/ItemsService";
import { CloseOutlined, PlusOutlined } from "@ant-design/icons";
import { useHotkey } from "../../Context/HotKeyContext";
import EntryLevelKeys from "../../Common/EntryLevelKeys";
import AddOrganisationModel from "./AddOrganisationModel";

const AddSubUnitModel = ({
  isModalOpen,
  setOpenModel,
  id,
  setTrigger,
  setNewSubUnitResponse,
  organisationId,
  organisationName,
  placement,
}: IOrganisationDetails) => {
  const { showKeys } = useHotkey();
  const [form] = Form.useForm<ISubUnitRequestModel>();
  const autoCompleteRef = useRef<InputRef>(null);
  const { currentClient } = useClient();
  const [loading, setLoading] = useState<boolean>(false);
  const { openNotification } = useContext(notificationContext);
  const [orgList, setOrgList] = useState<IIdNameModel[]>([]);
  const [subUnitData, setAllSubUnitData] = useState<string[]>([]);
  const [duplicateData, setDuplicateData] = useState<boolean>(false);
  const [addOpenModel, setAddOpenModel] = useState<{
    type: string;
    param: any;
  }>({
    type: "",
    param: {},
  });
  const [newOrganisationResponse, setNewOrganisationResponse] =
    useState<IIdNameModel>({ id: "", name: "" });

  // Post Api
  const saveData = () => {
    form.validateFields().then(async (val: any) => {
      setLoading(true);
      const data = {
        ...val,
        orgId: val.orgId?.value,
      };
      console.log("data", data);
      await OrganisationsService.postSubUnit(currentClient?.id!, data, id!)
        .then((val: IApiResponse<IIdNameModel>) => {
          if (val.result) {
            openNotification(
              "success",
              `${val?.result?.name} sub unit ${
                id ? "updated" : "added"
              } successfully`
            );
            setOpenModel({
              type: "",
              param: {},
            });
            setNewSubUnitResponse &&
              setNewSubUnitResponse({ ...val.result, orgId: organisationId! });
            setLoading(false);
            setTrigger && setTrigger((prev) => !prev);
          } else {
            setLoading(false);
            openNotification("error", val.message);
          }
        })
        .catch((er) => {
          openNotification("error", er.message);
          setLoading(false);
        });
    });
  };
  useEffect(() => {
    (async () => {
      await OrganisationsService.getSubUnitOptionsList(currentClient?.id!).then(
        (data: ISubUnitOptionsList[] | null) => {
          setAllSubUnitData(data!.map((each) => each.name));
        }
      );
    })();
  }, []);

  //-----Add New Organization Data -----------//
  useEffect(() => {
    if (newOrganisationResponse.id.length > 0) {
      setOrgList((pre: IIdNameModel[]) => {
        let newData = [...pre, newOrganisationResponse];
        return newData;
      });
    }
  }, [newOrganisationResponse]);

  //----- newly added organisation data placed in form ----------//
  useEffect(() => {
    if (newOrganisationResponse?.id) {
      let exist = orgList.some(
        (e: IIdNameModel) => e.id === newOrganisationResponse.id
      );
      if (exist)
        form.setFieldValue("orgId", {
          key: newOrganisationResponse.id,
          value: newOrganisationResponse.id,
          label: newOrganisationResponse.name,
          title: "",
        });
    }
  }, [orgList]);

  const getData = async () => {
    setLoading(true);
    await OrganisationsService.getSubUnit(currentClient?.id!, id!)
      .then((val: ISubUnitData | null) => {
        console.log(val);
        let formValues = {
          subUnitName: val?.subUnitName,
          orgId: { value: val?.orgId, label: val?.orgName },
        };
        console.log("formvalues", formValues);
        form.setFieldsValue(formValues);
        setLoading(false);
      })
      .catch((er) => {
        openNotification("error", er.message);
        setLoading(false);
      });
  };

  useEffect(() => {
    if (id) getData();
  }, [id]);

  const handleDuplicates = (subUnitName: string) => {
    let filteredData = subUnitData.filter(
      (e) => e.toLowerCase().trim() === subUnitName.toLowerCase().trim()
    );
    if (filteredData.length > 0) setDuplicateData(true);
    else setDuplicateData(false);
  };

  const getOrgList = async () => {
    if (organisationId) {
      form.setFieldValue("orgId", {
        key: organisationId,
        value: organisationId,
        label: organisationName,
        title: "",
      });
    } else {
      setLoading(true);
      await OrganisationsService.getOrgOptionsList(currentClient?.id!)
        .then((val) => {
          if (val) {
            setOrgList(val);
          }
          setLoading(false);
        })
        .catch((er) => {
          console.log("Data fetching error: ", er.message);
          setLoading(false);
        });
    }
  };
  useEffect(() => {
    getOrgList();
  }, [currentClient?.id]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (autoCompleteRef.current) {
        autoCompleteRef.current.focus();
      }
    }, 100); // Adjust delay if needed

    return () => clearTimeout(timer);
  }, [orgList]); // Ensures autofocus after initial render

  EntryLevelKeys({
    save: () => saveData(),
    cancel: () =>
      setOpenModel({
        type: "",
        param: {},
      }),
  });
  return (
    <>
      <Drawer
        open={isModalOpen}
        onClose={() =>
          setOpenModel({
            type: "",
            param: {},
          })
        }
        placement={placement ?? "right"}
        title={`${id ? "Update" : "Add"} sub unit`}
        width={550}
        closable={false}
        maskClosable={false}
        extra={
          <Space>
            <CloseOutlined
              onClick={() =>
                setOpenModel({
                  type: "",
                  param: {},
                })
              }
            />
          </Space>
        }
        footer={
          <div style={{ textAlign: "right" }}>
            <Space>
              <Tooltip
                title={<p>ALT+C</p>}
                placement="bottomLeft"
                open={showKeys}
              >
                <Button
                  disabled={loading}
                  onClick={() => {
                    setOpenModel({
                      type: "",
                      param: {},
                    });
                    form.resetFields();
                  }}
                >
                  Cancel
                </Button>
              </Tooltip>

              <Tooltip
                title={<p>ALT+S</p>}
                placement="bottomLeft"
                open={showKeys}
              >
                <Button
                  disabled={loading}
                  loading={loading}
                  type="primary"
                  onClick={() => saveData()}
                >
                  {`${id ? "Update" : "Save"}`}
                </Button>
              </Tooltip>
            </Space>
          </div>
        }
      >
        <Form name="subunit" form={form} disabled={loading} layout="vertical">
          <Form.Item
            name="subUnitName"
            label="Sub Unit's Name"
            rules={[
              { required: true },
              {
                pattern:
                  /^(?!.* {2})([A-Za-z][A-Za-z0-9_()&\-., ]{1,48}[A-Za-z0-9])$/,
                message: "Value's length must be between 3 and 50",
              },
              {
                validator: () => {
                  if (duplicateData)
                    return Promise.reject("Sub Unit Name already exist");
                  else return Promise.resolve();
                },
              },
            ]}
            className="input-item"
          >
            <Input
              type="text"
              maxLength={50}
              minLength={3}
              placeholder="Enter name here..."
              onChange={(e) => handleDuplicates(e.target.value)}
              ref={autoCompleteRef}
            ></Input>
          </Form.Item>
          <Form.Item
            name="orgId"
            label="Organisation"
            rules={[{ required: true, message: "Please select organisation" }]}
            className="input-item"
          >
            <Select
              labelInValue
              placeholder="Select organisation"
              options={orgList.map((org: IIdNameModel) => ({
                label: org.name,
                value: org.id,
                key: org.id,
                title: "",
              }))}
              disabled={organisationId ? true : false}
              dropdownRender={(orgList) => (
                <>
                  {orgList}
                  <Divider style={{ margin: "5px 0px" }} />
                  <Button
                    style={{ width: "100%" }}
                    icon={<PlusOutlined />}
                    type="link"
                    onClick={() =>
                      setAddOpenModel({
                        type: "Organisation",
                        param: {},
                      })
                    }
                  >
                    New Organisation
                  </Button>
                </>
              )}
            />
          </Form.Item>
        </Form>
      </Drawer>
      {addOpenModel.type === "Organisation" && (
        <AddOrganisationModel
          isModalOpen={true}
          setOpenModel={setAddOpenModel}
          setNewResponse={setNewOrganisationResponse}
          placement={"left"}
        />
      )}
    </>
  );
};

export default AddSubUnitModel;
